// Generat automat din README.md - Phase 3: Advanced Reporting Engine
import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Filter,
  Download,
  Calendar,
  BarChart3,
  PieChart,
  LineChart,
  TrendingUp,
  Users,
  DollarSign,
  Target,
  Settings,
  Eye,
  Share,
  Save,
  Plus,
  Edit,
  Trash2,
  Clock,
  Building,
  Mail,
  Zap,
  Database,
  RefreshCw,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import Button from '../../components/Button';

const AdvancedReportingEngine = () => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('templates');

  // Pre-built report templates
  const reportTemplates = [
    {
      id: 1,
      name: 'Executive Summary Report',
      description: 'Comprehensive overview of all key business metrics and KPIs',
      category: 'Executive',
      icon: TrendingUp,
      frequency: 'Monthly',
      recipients: 12,
      lastGenerated: '2024-03-15',
      status: 'active',
      metrics: ['Revenue', 'Customers', 'Conversion', 'Satisfaction'],
      estimatedTime: '5 min',
      complexity: 'High'
    },
    {
      id: 2,
      name: 'Sales Performance Dashboard',
      description: 'Detailed sales analytics with pipeline and forecast data',
      category: 'Sales',
      icon: DollarSign,
      frequency: 'Weekly',
      recipients: 8,
      lastGenerated: '2024-03-16',
      status: 'active',
      metrics: ['Sales Volume', 'Pipeline Value', 'Conversion Rate', 'Team Performance'],
      estimatedTime: '3 min',
      complexity: 'Medium'
    },
    {
      id: 3,
      name: 'HR Analytics Report',
      description: 'Employee satisfaction, performance metrics, and retention data',
      category: 'Human Resources',
      icon: Users,
      frequency: 'Bi-weekly',
      recipients: 5,
      lastGenerated: '2024-03-14',
      status: 'active',
      metrics: ['Employee Satisfaction', 'Turnover Rate', 'Performance Scores'],
      estimatedTime: '4 min',
      complexity: 'Medium'
    },
    {
      id: 4,
      name: 'Financial Analysis Report',
      description: 'P&L analysis, cash flow, and financial forecasting',
      category: 'Finance',
      icon: BarChart3,
      frequency: 'Monthly',
      recipients: 15,
      lastGenerated: '2024-03-13',
      status: 'draft',
      metrics: ['Revenue', 'Expenses', 'Profit Margin', 'Cash Flow'],
      estimatedTime: '7 min',
      complexity: 'High'
    },
    {
      id: 5,
      name: 'Marketing Campaign Analysis',
      description: 'ROI analysis for marketing campaigns and channel performance',
      category: 'Marketing',
      icon: Target,
      frequency: 'Weekly',
      recipients: 6,
      lastGenerated: '2024-03-16',
      status: 'active',
      metrics: ['Campaign ROI', 'Lead Generation', 'Channel Performance'],
      estimatedTime: '3 min',
      complexity: 'Medium'
    }
  ];

  // Custom reports created by users
  const customReports = [
    {
      id: 6,
      name: 'Customer Churn Analysis',
      description: 'Custom analysis for identifying at-risk customers',
      author: 'Maria Popescu',
      created: '2024-03-10',
      lastRun: '2024-03-16',
      runs: 23,
      type: 'custom',
      metrics: ['Customer Lifetime Value', 'Churn Probability', 'Usage Patterns'],
      estimatedTime: '6 min'
    },
    {
      id: 7,
      name: 'Product Usage Analytics',
      description: 'Feature adoption and user behavior analysis',
      author: 'Ion Georgescu',
      created: '2024-02-28',
      lastRun: '2024-03-15',
      runs: 18,
      type: 'custom',
      metrics: ['Feature Usage', 'User Engagement', 'Session Duration'],
      estimatedTime: '4 min'
    }
  ];

  // Report generation history
  const reportHistory = [
    { id: 1, name: 'Executive Summary Report', generated: '2024-03-16 09:30', by: 'System', status: 'completed', size: '2.4 MB' },
    { id: 2, name: 'Sales Performance Dashboard', generated: '2024-03-16 08:15', by: 'Ana Popescu', status: 'completed', size: '1.8 MB' },
    { id: 3, name: 'Customer Churn Analysis', generated: '2024-03-15 16:45', by: 'Maria Popescu', status: 'completed', size: '3.1 MB' },
    { id: 4, name: 'Financial Analysis Report', generated: '2024-03-15 14:20', by: 'System', status: 'failed', size: '-' },
    { id: 5, name: 'Marketing Campaign Analysis', generated: '2024-03-15 11:30', by: 'Ion Georgescu', status: 'completed', size: '1.5 MB' }
  ];

  // Available data sources
  const dataSources = [
    { id: 'crm', name: 'CRM Database', connected: true, lastSync: '2024-03-16 10:30', records: '12,847' },
    { id: 'sales', name: 'Sales Platform', connected: true, lastSync: '2024-03-16 10:25', records: '8,934' },
    { id: 'hr', name: 'HR Management', connected: true, lastSync: '2024-03-16 09:15', records: '2,156' },
    { id: 'finance', name: 'Accounting System', connected: true, lastSync: '2024-03-16 10:00', records: '15,678' },
    { id: 'marketing', name: 'Marketing Automation', connected: false, lastSync: '2024-03-15 18:30', records: '5,432' }
  ];

  // New report form state
  const [newReport, setNewReport] = useState({
    name: '',
    description: '',
    category: '',
    metrics: [],
    dataSources: [],
    filters: {},
    schedule: 'manual',
    recipients: []
  });

  // Handle report generation
  const handleGenerateReport = async (reportId) => {
    setIsGenerating(true);
    setSelectedReport(reportId);
    
    // Simulate report generation
    setTimeout(() => {
      setIsGenerating(false);
      alert('Report generated successfully! Check your downloads folder.');
    }, 3000);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const variants = {
      'active': 'success',
      'draft': 'warning',
      'inactive': 'default',
      'completed': 'success',
      'failed': 'error',
      'processing': 'info'
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  // Get complexity color
  const getComplexityColor = (complexity) => {
    switch(complexity) {
      case 'High': return 'text-red-600 bg-red-50';
      case 'Medium': return 'text-yellow-600 bg-yellow-50';
      case 'Low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <FileText className="w-8 h-8 mr-3 text-purple-500" />
            Advanced Reporting Engine
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Generate custom reports cu AI-powered insights și automated delivery
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => setShowFiltersModal(true)}
            className="flex items-center space-x-2"
            variant="outline"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
          </Button>
          
          <Button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 bg-purple-500 hover:bg-purple-600 text-white"
          >
            <Plus className="w-4 h-4" />
            <span>Create Report</span>
          </Button>
        </div>
      </div>

      {/* Data Sources Status */}
      <Card title="Data Sources Status" icon={Database}>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {dataSources.map((source) => (
            <div key={source.id} className="p-3 border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900 dark:text-white text-sm">{source.name}</span>
                <div className={`w-2 h-2 rounded-full ${source.connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div>Records: {source.records}</div>
                <div>Sync: {source.lastSync}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8">
          {[
            { id: 'templates', label: 'Report Templates', count: reportTemplates.length },
            { id: 'custom', label: 'Custom Reports', count: customReports.length },
            { id: 'history', label: 'Generation History', count: reportHistory.length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </nav>
      </div>

      {/* Report Templates Tab */}
      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reportTemplates.map((report) => {
            const Icon = report.icon;
            return (
              <Card key={report.id} className="hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Icon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">{report.name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{report.description}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Category:</span>
                    <span className="font-medium">{report.category}</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Frequency:</span>
                    <span className="font-medium">{report.frequency}</span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Recipients:</span>
                    <span className="font-medium">{report.recipients}</span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Est. Time:</span>
                    <span className={`px-2 py-1 rounded text-xs ${getComplexityColor(report.complexity)}`}>
                      {report.estimatedTime}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
                    {getStatusBadge(report.status)}
                  </div>

                  {/* Metrics Preview */}
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Key Metrics:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {report.metrics.slice(0, 3).map((metric) => (
                        <span key={metric} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-xs rounded">
                          {metric}
                        </span>
                      ))}
                      {report.metrics.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-xs rounded">
                          +{report.metrics.length - 3}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <button
                      onClick={() => handleGenerateReport(report.id)}
                      disabled={isGenerating && selectedReport === report.id}
                      className="flex-1 bg-purple-500 hover:bg-purple-600 text-white py-2 px-3 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center space-x-1"
                    >
                      {isGenerating && selectedReport === report.id ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" />
                          <span>Generate</span>
                        </>
                      )}
                    </button>
                    
                    <button className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <Eye className="w-4 h-4" />
                    </button>
                    
                    <button className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <Edit className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Custom Reports Tab */}
      {activeTab === 'custom' && (
        <div className="space-y-4">
          {customReports.map((report) => (
            <Card key={report.id} className="hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{report.name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{report.description}</p>
                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                      <span>By: {report.author}</span>
                      <span>Created: {report.created}</span>
                      <span>Runs: {report.runs}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Est. Time: {report.estimatedTime}</span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleGenerateReport(report.id)}
                      className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1"
                    >
                      <Download className="w-4 h-4" />
                      <span>Run</span>
                    </button>
                    <button className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button className="px-3 py-2 border border-red-300 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}

          {/* Add Custom Report Button */}
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
            <div className="flex flex-col items-center">
              <Plus className="w-12 h-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Create Custom Report</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">Build your own report with custom metrics and filters</p>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card title="Report Generation History" icon={Clock}>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Report Name</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Generated</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Generated By</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Size</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reportHistory.map((record) => (
                  <tr key={record.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="py-3 px-4">
                      <span className="font-medium text-gray-900 dark:text-white">{record.name}</span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {record.generated}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {record.by}
                    </td>
                    <td className="py-3 px-4">
                      {getStatusBadge(record.status)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {record.size}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex space-x-2">
                        {record.status === 'completed' && (
                          <button className="text-blue-600 hover:text-blue-800 text-sm">
                            Download
                          </button>
                        )}
                        <button className="text-gray-600 hover:text-gray-800 text-sm">
                          Details
                        </button>
                        {record.status === 'failed' && (
                          <button className="text-green-600 hover:text-green-800 text-sm">
                            Retry
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Create Report Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Custom Report"
      >
        <div className="space-y-6 max-h-96 overflow-y-auto">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Report Name
              </label>
              <input
                type="text"
                value={newReport.name}
                onChange={(e) => setNewReport({...newReport, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800"
                placeholder="Enter report name..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category
              </label>
              <select
                value={newReport.category}
                onChange={(e) => setNewReport({...newReport, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800"
              >
                <option value="">Select category...</option>
                <option value="Executive">Executive</option>
                <option value="Sales">Sales</option>
                <option value="Marketing">Marketing</option>
                <option value="Finance">Finance</option>
                <option value="HR">Human Resources</option>
                <option value="Operations">Operations</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={newReport.description}
              onChange={(e) => setNewReport({...newReport, description: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800"
              placeholder="Describe what this report will analyze..."
            />
          </div>

          {/* Data Sources Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Data Sources
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {dataSources.map((source) => (
                <label key={source.id} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newReport.dataSources.includes(source.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewReport({
                          ...newReport,
                          dataSources: [...newReport.dataSources, source.id]
                        });
                      } else {
                        setNewReport({
                          ...newReport,
                          dataSources: newReport.dataSources.filter(id => id !== source.id)
                        });
                      }
                    }}
                    disabled={!source.connected}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className={`text-sm ${!source.connected ? 'text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
                    {source.name}
                  </span>
                  {!source.connected && (
                    <span className="text-xs text-red-500">(Offline)</span>
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              onClick={() => setShowCreateModal(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                console.log('Creating report:', newReport);
                setShowCreateModal(false);
                alert('Custom report created successfully!');
                setNewReport({ name: '', description: '', category: '', metrics: [], dataSources: [], filters: {}, schedule: 'manual', recipients: [] });
              }}
              className="bg-purple-500 hover:bg-purple-600 text-white"
            >
              Create Report
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdvancedReportingEngine;